// @ts-strict-ignore
import { workflowNodeType } from 'flow/service/graph';
import { ActionType } from '../../engine/constants';
import type {
  Action,
  NodeEditorTab,
  PublicId,
  PendingParenthesisInfo,
} from '../../engine/types';
import type { WorkflowState } from '../state';
import { initialState } from '../state';
import type { EditorContainerType } from '../constants';
import type { RuleBuilderRule } from 'flow/service/rules';
import { isActionOfType } from 'flow/service/actions';
import { getFirstSnippetParentId, isExternalActionType } from 'flow/service/actions/snippets';


// ================ UI State Reducers ================
export function setFocusedNodeId(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['focusedNodeId'] },
) {
  const nodeId = action.payload;
  const actionForNodeId = state.workflowData.actions[nodeId];
  let defaultTabForNodeType: NodeEditorTab = 'rule';
  if (isActionOfType(actionForNodeId, ActionType.INPUT)) {
    defaultTabForNodeType = 'configuration';
    // expand the node folder when input is focused
    state.ui.nodeContainers.inputAttribute.isCollapsed = false;
  }
  if (isActionOfType(actionForNodeId, ActionType.OUTCOME)) {
    defaultTabForNodeType = null;
  }

  // if the action belongs to a snippet - expand the snippet
  const containerId = getFirstSnippetParentId(actionForNodeId?.containerContext);
  if (containerId && !isExternalActionType(actionForNodeId?.type)) {
    state.ui.nodeContainers.snippets[containerId] = {
      isCollapsed: false,
    };
  }

  state.ui.focusedNodeId = nodeId;
  state.ui.highlightedNodeId = null;
  // reset the selected evidence threshold id when the focused node changes anywhere every time
  state.workflowFeatures.ruleExplainability.thresholdId = null;
  /*
   * Whenever the focused node changes, we want to re-adjust the selected tab
   * back to the default tab for that action
   */
  state.ui.activeNodeEditorTab = defaultTabForNodeType;
}

export function setActiveNodeEditorTab(
  state: WorkflowState,
  action: { payload: NodeEditorTab },
) {
  state.ui.activeNodeEditorTab = action.payload;
}

export function setDiffMode(
  state: WorkflowState,
  action: { payload: boolean },
) {
  state.ui.isInDiffMode = action.payload;
}

export function setIsTagSwapMode(
  state: WorkflowState,
  action: { payload: boolean },
) {
  state.ui.isTagSwapMode = { isActive: action.payload };
}

export function resetTagSwapMode(state: WorkflowState) {
  state.ui.isTagSwapMode = { isActive: false };
}

export function setHoveredNodeId(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['hoveredNodeId'] },
) {
  state.ui.hoveredNodeId = action.payload;
}

export function setConnectorInteractionState(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['connector']['interactionState'] },
) {
  state.ui.connector.interactionState = action.payload;
}

export function setConnectorOwnerNodeId(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['connector']['ownerNodeId'] },
) {
  state.ui.connector.ownerNodeId = action.payload;
}

export function setConnectorCanCreateItemStatus(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['connector']['canCreateItem'] },
) {
  state.ui.connector.canCreateItem = action.payload;
}

export function setHighlightedNodeId(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['highlightedNodeId'] },
) {
  state.ui.highlightedNodeId = action.payload;
}

export function setFloatedAnchorNodeId(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['floatedAnchorNodeId'] },
) {
  state.ui.floatedAnchorNodeId = action.payload;
}

export function setNodeFolderCollapsed(
  state: WorkflowState,
  action: {
    payload: {
      isCollapsed: boolean;
    };
  },
) {
  const { isCollapsed } = action.payload;
  // currently only used for input attributes
  state.ui.nodeContainers[workflowNodeType.inputAttribute].isCollapsed = isCollapsed;
}

export function setSnippetCollapsed(
  state: WorkflowState,
  action: {
    payload: {
      isCollapsed: boolean;
      snippetId: PublicId;
    };
  },
) {
  const { isCollapsed, snippetId } = action.payload;

  if (!state.ui.nodeContainers.snippets[snippetId]) {
    state.ui.nodeContainers.snippets[snippetId] = {
      isCollapsed: false,
    };
  }

  state.ui.nodeContainers.snippets[snippetId].isCollapsed = isCollapsed;
}

export function initSnippetCollapsedState(
  state: WorkflowState,
  action: { payload: { actions: Action[] } },
) {
  const { actions } = action.payload;
  actions.forEach(snippetAction => {
    if (isActionOfType(snippetAction, ActionType.SNIPPET)) {
      state.ui.nodeContainers.snippets[snippetAction.publicId] = {
        isCollapsed: true,
      };
    }
  });
}

export function resetNodeAndConnectorState(state: WorkflowState) {
  state.ui.activeNodeEditorTab = initialState.ui.activeNodeEditorTab;
  state.ui.focusedNodeId = initialState.ui.focusedNodeId;
  state.ui.hoveredNodeId = initialState.ui.hoveredNodeId;
  state.ui.highlightedNodeId = initialState.ui.highlightedNodeId;
  state.ui.floatedAnchorNodeId = initialState.ui.floatedAnchorNodeId;
  state.ui.connector = { ...initialState.ui.connector };
}

export function setShouldRenderCriticalNodePath(
  state: WorkflowState,
  action: {
    payload: WorkflowState['ui']['shouldRenderCriticalNodePath'];
  },
) {
  state.ui.shouldRenderCriticalNodePath = action.payload;
}

export function setEditorContainerType(
  state: WorkflowState,
  action: { payload: EditorContainerType },
) {
  state.ui.editorContainerType = action.payload;
}

export function updateCanvasProperties(
  state: WorkflowState,
  action: { payload: Partial<WorkflowState['ui']['canvasProperties']> },
) {
  state.ui.canvasProperties = {
    ...state.ui.canvasProperties,
    ...action.payload,
    rect: {
      ...state.ui.canvasProperties.rect,
      ...action.payload.rect,
    },
  };
}

// ================ Linked Child Action State Reducers ================

export function updateLinkedChildActionThresholdIds(
  state: WorkflowState,
  action: {
    payload: {
      thresholdId: WorkflowState['linkedChildAction']['thresholdIds'][number];
      operation: 'add' | 'remove';
    };
  },
) {
  const { thresholdId, operation } = action.payload;

  if (operation === 'add') {
    state.linkedChildAction.thresholdIds = [...state.linkedChildAction.thresholdIds, thresholdId];
  } else {
    state.linkedChildAction.thresholdIds = state.linkedChildAction.thresholdIds
      .filter(id => id !== thresholdId);
  }
}

export function setLinkedChildActionOwnerNodeId(
  state: WorkflowState,
  action: { payload: WorkflowState['linkedChildAction']['ownerNodeId'] },
) {
  state.linkedChildAction.ownerNodeId = action.payload;
}

export function setLinkedChildActionCreateActionPayload(
  state: WorkflowState,
  action: { payload: WorkflowState['linkedChildAction']['createActionPayload'] },
) {
  state.linkedChildAction.createActionPayload = action.payload;
}

export function resetLinkedChildActionState(
  state: WorkflowState,
) {
  state.linkedChildAction = initialState.linkedChildAction;
}


// ================ Global Component State Reducers ================
export function resetComponentState(
  state: WorkflowState,
) {
  state.ui.components = initialState.ui.components;
}

export function setServiceRoutingCategoryNameInEditing(
  state: WorkflowState,
  action: { payload: string },
) {
  state.ui.components.services.serviceRoutingCategoryNameInEditing = action.payload;
}

export function setServiceActionOptionPublicIdInEditing(
  state: WorkflowState,
  action: { payload: string },
) {
  state.ui.components.services.serviceActionOptionPublicIdInEditing = action.payload;
}

// ================ Search State Reducers ================
export function setIsSearchBoxVisible(
  state: WorkflowState,
  action: { payload: boolean },
) {
  state.ui.search.isSearchBoxVisible = action.payload;
}

export function setSearchQuery(
  state: WorkflowState,
  action: { payload: string },
) {
  state.ui.search.query = action.payload;
}

export function setSearchMatchIndex(
  state: WorkflowState,
  action: { payload: number },
) {
  state.ui.search.matchIndex = action.payload;
}

// ================ Rule Builder State Reducers ================

export function setRuleBuilderActiveRuleNumberDropdownId(
  state: WorkflowState,
  action: { payload: { instanceId: string; index: number } | null },
) {
  state.ui.ruleBuilder.activeRuleNumberDropdown = action.payload;
}

export function setRuleBuilderIsDragging(
  state: WorkflowState,
  action: { payload: boolean },
) {
  state.ui.ruleBuilder.isDragging = action.payload;
}

export function setRuleBuilderHighlightedRuleId(
  state: WorkflowState,
  action: { payload: RuleBuilderRule['id'] | null },
) {
  state.ui.ruleBuilder.highlightedRuleId = action.payload;
}

export function setRuleBuilderHoveredRuleIndex(
  state: WorkflowState,
  action: { payload: { instanceId: string; index: number } | null },
) {
  state.ui.ruleBuilder.hoveredRuleIndex = action.payload;
}

export function setRuleBuilderPendingGroupInfo(
  state: WorkflowState,
  action: {
    payload: {
      parentRuleId: RuleBuilderRule['id'] | null;
      startIndex: number | null;
    };
  },
) {
  state.ui.ruleBuilder.pendingGroup = action.payload;
}

export function setRuleBuilderPanelBoxContainerWidth(
  state: WorkflowState,
  action: { payload: number },
) {
  state.ui.ruleBuilder.panelBoxContainerWidth = action.payload;
}

export function setRuleBuilderActiveSubEditor(
  state: WorkflowState,
  action: { payload: WorkflowState['ui']['ruleBuilder']['activeSubEditor'] },
) {
  state.ui.ruleBuilder.activeSubEditor = action.payload;
}

// ================ Expression Editor State Reducers ================

export function setPendingGroup(
  state: WorkflowState,
  action: { payload: PendingParenthesisInfo },
) {
  state.ui.expressionBuilder.pendingGroup = action.payload;
}

export function resetPendingGroup(state: WorkflowState) {
  state.ui.expressionBuilder.pendingGroup = (
    initialState.ui.expressionBuilder.pendingGroup
  );
}
